// Pure helpers + constants for the contractor Daily Note + eMAR form.
// Extracted from `pages/DailyNoteForm.jsx` to keep that file focused on the
// stateful page composition. Anything in here is intentionally side-effect
// free so it can be unit-tested in isolation later if needed.

export const pad = (n) => String(n).padStart(2, "0");
export const todayISO = () => new Date().toISOString().slice(0, 10);
export const STATUSES = ["administered", "missed", "none"];

// v17 — fixed daily compliance verifications (rendered for every overnight=Yes day)
export const COMPLIANCE_ITEMS = [
  { id: "daily_routine_followed", label: "Standard daily routine followed" },
  { id: "meds_administered_verified", label: "Medications administered as scheduled and verified" },
  { id: "health_status_monitored", label: "Health status monitored and oversight maintained" },
];

// Goal status — Improved / Maintained / Regressed
export const GOAL_STATUSES = [
  { value: "improved", label: "Improved", color: "border-emerald-600 bg-emerald-50 text-emerald-700" },
  { value: "maintained", label: "Maintained", color: "border-slate-500 bg-slate-50 text-slate-700" },
  { value: "regressed", label: "Regressed", color: "border-red-600 bg-red-50 text-red-700" },
];

// v31 — split a goal's "Support Details / Description" + structured support_items
// into individual support lines. Splits on newlines, bullet markers (•/-/*), and
// numbered list prefixes (1. / 1)). De-duplicates while preserving order.
//
// v35 — when a goal already has structured `support_items`, treat that as the
// authoritative list and IGNORE the prose `description` so a summary heading
// like "Goal 1 — Activity engagement…" never gets surfaced as an extra
// trackable Yes/No row alongside the real strategies. Only fall back to
// parsing the description when no structured items exist.
export const splitSupportLines = (description, supportItems, goalTitle) => {
  const out = [];
  const seen = new Set();
  const titleKey = (goalTitle || "").trim().toLowerCase();
  const push = (raw) => {
    const line = (raw || "")
      .replace(/^[\s\u2022•\-\*\u2013\u2014]+/, "")   // leading bullet markers
      .replace(/^\(?\d+[.)]\s+/, "")                  // leading numbered list "1. " / "1) "
      .trim();
    if (!line) return;
    const key = line.toLowerCase();
    if (seen.has(key)) return;
    // Don't echo the goal title itself as a support strategy row.
    if (titleKey && key === titleKey) return;
    seen.add(key);
    out.push(line);
  };
  const structured = (supportItems || []).filter((s) => (s || "").trim());
  if (structured.length > 0) {
    structured.forEach(push);
    return out;
  }
  if (description) {
    description
      .split(/\r?\n|(?:\s\u2022\s)|(?:\s-\s)/g) // newlines or inline " • " / " - " separators
      .forEach(push);
  }
  return out;
};

// v25 — derive which clock-slots a medication actually uses based on its
// `schedule` string. "AM only" / "PM only" / "AM/PM" / "Bedtime" / "TID" etc.
// Used by the eMAR grid so contractors don't see fillable cells for slots
// that aren't part of the regimen.
export const medUsesSlot = (m, slot) => {
  const s = String(m?.schedule || "").trim();
  if (m?.is_prn || s.includes("PRN")) return false;

  if (slot === "am") {
    if (s === "AM Only" || s === "Both (AM & PM)") return true;
    const low = s.toLowerCase();
    if (low.includes("pm only")) return false;
    if (low.includes("am") || low.includes("morning") || low.includes("bid") || low.includes("tid") || low === "daily" || low.includes("every")) return true;
    return false;
  }
  
  if (slot === "pm") {
    if (s === "PM Only" || s === "Both (AM & PM)") return true;
    const low = s.toLowerCase();
    if (low.includes("am only")) return false;
    if (low.includes("pm") || low.includes("evening") || low.includes("night") || low.includes("bedtime") || low.includes("bid") || low.includes("tid") || low === "daily" || low.includes("every")) return true;
    return false;
  }
  return false;
};
